import { useEffect, useState } from "react";
import { checkSampleData, getUsersApi, purgeSampleData, useCore } from "@praesto/core";
import { Banner, Button, ConfirmDialog } from "@praesto/ui-kit";

/**
 * Dismissible nudge shown while ANY `sample: true` doc remains in the tenant
 * (sample departments not confirmed in the onboarding wizard + the sample
 * SOPs, which the wizard never touches). Detected via the owner-only
 * `GET /api/users/sample-data` check (mirrors the same query the purge
 * endpoint runs, without deleting anything).
 *
 * Dismissal is local-only (component state, no persistence) — reappears on
 * next mount/reload as long as sample data is still there, by design: this
 * is a nudge, not a one-time notice.
 */
export function SampleDataBanner() {
  const core = useCore();
  const t = core.i18n.t;
  const [hasSampleData, setHasSampleData] = useState(false);
  const [dismissed, setDismissed] = useState(false);
  const [confirming, setConfirming] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      const api = getUsersApi();
      const tok = await api.getAuthToken();
      const r = await checkSampleData(api.apiBase, tok);
      if (!cancelled) setHasSampleData(r.hasSampleData);
    })().catch(() => undefined);
    return () => {
      cancelled = true;
    };
  }, []);

  async function purge() {
    setBusy(true);
    setError(null);
    try {
      const api = getUsersApi();
      const tok = await api.getAuthToken();
      await purgeSampleData(api.apiBase, tok);
      setHasSampleData(false);
      setConfirming(false);
    } catch (e) {
      setError(String(e));
    } finally {
      setBusy(false);
    }
  }

  if (!hasSampleData || dismissed) return null;

  // A lasting condition is a Banner (soft warning), with its action inside
  // and an explicit dismiss — the ui-kit Banner carries the v18 ink family.
  return (
    <>
      <Banner
        tone="warning"
        actions={
          <Button variant="secondary" size="sm" onClick={() => setConfirming(true)}>
            {t("sampleData.banner.action")}
          </Button>
        }
        onDismiss={() => setDismissed(true)}
      >
        {t("sampleData.banner.message")}
        {error && <span className="ml-2 text-danger">{error}</span>}
      </Banner>
      <ConfirmDialog
        open={confirming}
        title={t("sampleData.banner.confirmTitle")}
        message={t("sampleData.banner.confirmBody")}
        confirmLabel={t("sampleData.banner.confirmAction")}
        busy={busy}
        onConfirm={() => void purge()}
        onCancel={() => setConfirming(false)}
      />
    </>
  );
}
